import React, { useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import {
  buildCPForm52OfMRCData,
  createCPForm52OfMRCPdfDoc,
} from "./downloadCPForm52OfMRCPdf";
import {
  calcUnitsCharged,
  formatDisplayValue,
  formatUnitsAdjDisplay,
  formatUnitsChargedDisplay,
  getCp52MeterReadingRows,
  isMdiMeterRow,
  resolveCp52MeterToBeReading,
  resolveCp52MeterUnitsAdjusted,
} from "./mrcReadingUtils";
import "./MrcCp52PrepareDialog.scss";

const cell = (v) => (v === "" || v == null ? "—" : String(v));

const isNegativeAmount = (value) => {
  const n = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) && n < 0;
};

const EMP_COLS = [
  "D.D. Officer Code",
  "TR Sur",
  "Income Tax",
  "Var Chrg",
  "EQ Sur",
  "FC Sur",
  "G.S.T",
  "NJ Sur",
  "AQA",
  "AQTA2",
  "AQTA3",
  "ADMC",
  "Total Amnt",
  "Units Adj.",
];

const SUBSIDY_GROUPS = [
  { title: "Federal", subs: ["Elect. Duty", "OTHERS", "NJ Sur", "FC-Sur"] },
  { title: "Provincial", subs: ["Elect. Duty", "OTHERS", "NJ Sur", "FC-Sur"] },
  { title: "Local Bodies", subs: ["ED.", "OTHERS"] },
  { title: "WAPDA", subs: ["Elect. Duty", "OTHERS", "NJ Sur", "FC-Sur"] },
];

const SUBSIDY_TRAILING = [
  "ITS-Tax",
  "FC-Sur",
  "TR-Sur",
  "R-Stax",
  "Addl. Stax",
  "D-Itax",
  "ISP-2019",
  "ZRIR",
  "AQA",
  "AQTA2",
  "ADMC",
  "AQT3",
];

const buildSubsidyHeaders = () => {
  const headers = [];
  SUBSIDY_GROUPS.forEach((g) =>
    g.subs.forEach((s) => headers.push({ group: g.title, label: s }))
  );
  SUBSIDY_TRAILING.forEach((t) => headers.push({ group: "", label: t }));
  return headers;
};

const buildSubsidyValues = (src, headers) => {
  const vals = headers.map(() => "");
  const fcIdx = headers.findIndex(
    (h, idx) =>
      h.label === "FC-Sur" && idx >= headers.length - SUBSIDY_TRAILING.length
  );
  const aqaIdx = headers.findIndex((h) => h.label === "AQA");
  const aqta2Idx = headers.findIndex((h) => h.label === "AQTA2");
  if (fcIdx >= 0) vals[fcIdx] = src?.fcSur ?? "";
  if (aqaIdx >= 0) vals[aqaIdx] = src?.aqa ?? "";
  if (aqta2Idx >= 0) vals[aqta2Idx] = src?.aqta2 ?? "";
  return vals;
};

const MrcCp52PrepareDialog = ({
  open,
  onClose,
  onConfirmPrepared,
  onOpenBill,
  pdfOptions = {},
  documents = [],
}) => {
  const d = useMemo(
    () => buildCPForm52OfMRCData(pdfOptions.data || {}),
    [pdfOptions.data]
  );

  const meterDisplayRows = useMemo(
    () => getCp52MeterReadingRows(documents, d),
    [documents, d]
  );

  const chargeCols = d.chargeCols || [];
  const subsidyHeaders = useMemo(() => buildSubsidyHeaders(), []);
  const subsidyCharged = useMemo(
    () => buildSubsidyValues(d.subsidyCharged, subsidyHeaders),
    [d.subsidyCharged, subsidyHeaders]
  );
  const subsidyToBe = useMemo(
    () => buildSubsidyValues(d.subsidyToBeCharged, subsidyHeaders),
    [d.subsidyToBeCharged, subsidyHeaders]
  );
  const subsidyAdj = useMemo(
    () => buildSubsidyValues(d.subsidyToBeAdjusted, subsidyHeaders),
    [d.subsidyToBeAdjusted, subsidyHeaders]
  );

  const handleDownloadPdf = () => {
    try {
      const doc = createCPForm52OfMRCPdfDoc({
        ...pdfOptions,
        documents: documents?.length ? documents : pdfOptions.documents || [],
      });
      doc.save("CP-Form-52-of-MRC.pdf");
    } catch {
      // silent — table still usable if PDF build fails
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="mrc-cp52-prepare-dialog"
      maxWidth={false}
      fullWidth
      PaperProps={{ className: "mrc-cp52-prepare-dialog__paper" }}
    >
      <DialogTitle className="mrc-cp52-prepare-dialog__title">
        <Typography className="mrc-cp52-prepare-dialog__heading">
          CP Form 52 of MRC — Adjustment Note
        </Typography>
        <div className="mrc-cp52-prepare-dialog__title-actions">
          {typeof onOpenBill === "function" ? (
            <Button
              type="button"
              variant="outlined"
              size="small"
              className="mrc-cp52-prepare-dialog__pdf-btn"
              startIcon={<DescriptionIcon fontSize="small" />}
              onClick={onOpenBill}
            >
              Bill
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outlined"
            size="small"
            className="mrc-cp52-prepare-dialog__pdf-btn"
            startIcon={<PictureAsPdfIcon fontSize="small" />}
            onClick={handleDownloadPdf}
          >
            Download PDF
          </Button>
          <button
            type="button"
            className="mrc-cp52-prepare-dialog__close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </DialogTitle>

      <DialogContent className="mrc-cp52-prepare-dialog__content">
        <div className="mrc-cp52-form">
          {/* ===== Top reference strip (same as PDF) ===== */}
          <div className="mrc-cp52-form__scroll">
            <table className="mrc-cp52-form__top">
              <thead>
                <tr>
                  <th colSpan={3}>Reference Number</th>
                  <th colSpan={3}>Adjustment Note</th>
                  <th>Due Date</th>
                  <th>Billing Month</th>
                  <th>Extended Due Date</th>
                  <th>Tariff</th>
                  <th>Enq. No</th>
                  <th>Date</th>
                </tr>
                <tr className="mrc-cp52-form__subhead">
                  <th>Batch #</th>
                  <th>Sub Division</th>
                  <th>Account No</th>
                  <th>C</th>
                  <th>Number</th>
                  <th>Date</th>
                  <th rowSpan={2}>{cell(d.dueDate)}</th>
                  <th rowSpan={2}>{cell(d.billingMonth)}</th>
                  <th rowSpan={2}>{cell(d.extendedDueDate)}</th>
                  <th rowSpan={2}>{cell(d.tariffDisplay || d.tariffCode)}</th>
                  <th rowSpan={2}>{cell(d.enqNo)}</th>
                  <th rowSpan={2}>{cell(d.enqDate)}</th>
                </tr>
                <tr>
                  <td>{cell(d.batchNo)}</td>
                  <td>{cell(d.subDivision)}</td>
                  <td>{cell(d.accountNo)}</td>
                  <td>{cell(d.adjC)}</td>
                  <td>{cell(d.adjNumber)}</td>
                  <td>{cell(d.adjDate)}</td>
                </tr>
                <tr>
                  <td colSpan={6} className="mrc-cp52-form__filler" />
                  <th colSpan={6}>Name &amp; Address of Consumer</th>
                </tr>
                <tr>
                  <td colSpan={6} className="mrc-cp52-form__filler" />
                  <td colSpan={6} className="mrc-cp52-form__addr">
                    {cell(d.consumerName)}
                    {d.consumerAddress ? `, ${d.consumerAddress}` : ""}
                  </td>
                </tr>
              </thead>
            </table>
          </div>

          {/* ===== Charges ===== */}
          <div className="mrc-cp52-form__scroll">
            <table className="mrc-cp52-form__charges">
              <thead>
                <tr>
                  <th className="mrc-cp52-form__sticky" />
                  {chargeCols.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="mrc-cp52-form__sticky">Amount Charged</td>
                  {(d.amountCharged || []).map((v, i) => (
                    <td
                      key={`c-${i}`}
                      className={
                        String(v) !== "0" && String(v) !== ""
                          ? "is-hl"
                          : ""
                      }
                    >
                      {cell(v)}
                    </td>
                  ))}
                </tr>
                <tr className="is-alt">
                  <td className="mrc-cp52-form__sticky">
                    Amount to be Charged
                  </td>
                  {(d.amountToBeCharged || []).map((v, i) => (
                    <td
                      key={`t-${i}`}
                      className={
                        String(v) !== "0" && String(v) !== ""
                          ? "is-hl"
                          : ""
                      }
                    >
                      {cell(v)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="mrc-cp52-form__sticky">
                    Amount to be Adjusted
                  </td>
                  {(d.amountToBeAdjusted || []).map((v, i) => (
                    <td
                      key={`a-${i}`}
                      className={
                        String(v) !== "0" && String(v) !== ""
                          ? "is-hl"
                          : ""
                      }
                    >
                      {cell(v)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mrc-cp52-form__note-total">
            <p className="mrc-cp52-form__note">
              * G.S.T Subsidy is not included in Total Adjustments.
            </p>
            <table className="mrc-cp52-form__totals">
              <tbody>
                <tr>
                  <th>No. of Months</th>
                  <td>{cell(d.noOfMonths)}</td>
                  <th
                    className={`mrc-cp52-form__totals-adj-h${
                      isNegativeAmount(d.totalAdjustment)
                        ? " mrc-cp52-form__totals-adj-h--negative"
                        : ""
                    }`}
                  >
                    Total Adjustment
                  </th>
                  <td
                    className={`mrc-cp52-form__totals-adj${
                      isNegativeAmount(d.totalAdjustment)
                        ? " mrc-cp52-form__totals-adj--negative"
                        : ""
                    }`}
                  >
                    {cell(d.totalAdjustment)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ===== MRC + WAPDA Employees side by side ===== */}
          <div className="mrc-cp52-form__pair">
            <div className="mrc-cp52-form__pair-col">
              <div className="mrc-cp52-form__band">--------MRC--------</div>
              <div
                className={`mrc-cp52-form__scroll${
                  meterDisplayRows.length > 6
                    ? " mrc-cp52-form__scroll--compact-meter"
                    : ""
                }`}
              >
                <table className="mrc-cp52-form__meter">
                  <thead>
                    <tr>
                      <th>Meter ID For Gen.</th>
                      <th>Meter ID For MDI</th>
                      <th>Set No.</th>
                      <th>Meter No.</th>
                      <th>Present Reading As Charged</th>
                      <th>Present Reading To be Charged</th>
                      <th>Unit Adjusted</th>
                      <th>Unit Charged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meterDisplayRows.map((row) => {
                      const isMdi = isMdiMeterRow(row);
                      const toBe = resolveCp52MeterToBeReading(row, d);
                      const unitsAdj = resolveCp52MeterUnitsAdjusted(row, d);
                      const unitsCharged =
                        calcUnitsCharged(row.asCharged, row.previous, row.mf) ??
                        d.unitCharged;
                      return (
                        <tr key={row.id}>
                          <td>
                            {Number(row.meterId) === 1 || Number(row.meterId) === 2
                              ? String(row.meterId)
                              : "—"}
                          </td>
                          <td>
                            {Number(row.meterId) === 3 ? "3" : "—"}
                          </td>
                          <td>{formatDisplayValue(row.setNo)}</td>
                          <td>
                            {formatDisplayValue(row.meterSerialNo) ||
                              cell(d.meterNo)}
                          </td>
                          <td>
                            {formatDisplayValue(row.asCharged) ||
                              cell(d.presentAsCharged)}
                          </td>
                          <td>{isMdi ? "—" : formatDisplayValue(toBe)}</td>
                          <td className={isMdi || toBe == null ? "" : "is-hl"}>
                            {isMdi || toBe == null
                              ? "—"
                              : formatUnitsAdjDisplay(unitsAdj)}
                          </td>
                          <td>{formatUnitsChargedDisplay(unitsCharged)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mrc-cp52-form__pair-col">
              <div className="mrc-cp52-form__band">For WAPDA Employees</div>
              <div className="mrc-cp52-form__scroll">
                <table className="mrc-cp52-form__emp">
                  <thead>
                    <tr>
                      {EMP_COLS.map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {EMP_COLS.map((c) => (
                        <td key={c}> </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ===== Subsidies Adjustments ===== */}
          <div className="mrc-cp52-form__band mrc-cp52-form__band--full">
            Subsidies Adjustments
          </div>
          <div className="mrc-cp52-form__scroll">
            <table className="mrc-cp52-form__subsidy">
              <thead>
                <tr>
                  <th rowSpan={2} className="mrc-cp52-form__sticky" />
                  {SUBSIDY_GROUPS.map((g) => (
                    <th key={g.title} colSpan={g.subs.length}>
                      {g.title}
                    </th>
                  ))}
                  <th colSpan={SUBSIDY_TRAILING.length} />
                </tr>
                <tr className="mrc-cp52-form__subhead">
                  {subsidyHeaders.map((h, i) => (
                    <th key={`${h.group}-${h.label}-${i}`}>{h.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="mrc-cp52-form__sticky">Amount Charged</td>
                  {subsidyCharged.map((v, i) => (
                    <td
                      key={`sc-${i}`}
                      className={
                        String(v) !== "0" && String(v) !== "" ? "is-hl" : ""
                      }
                    >
                      {cell(v)}
                    </td>
                  ))}
                </tr>
                <tr className="is-alt">
                  <td className="mrc-cp52-form__sticky">Amt to be Charged</td>
                  {subsidyToBe.map((v, i) => (
                    <td
                      key={`st-${i}`}
                      className={
                        String(v) !== "0" && String(v) !== "" ? "is-hl" : ""
                      }
                    >
                      {cell(v)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="mrc-cp52-form__sticky">Amt to be Adjusted</td>
                  {subsidyAdj.map((v, i) => (
                    <td
                      key={`sa-${i}`}
                      className={
                        String(v) !== "0" && String(v) !== "" ? "is-hl" : ""
                      }
                    >
                      {cell(v)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>

      <DialogActions className="mrc-cp52-prepare-dialog__actions">
        <Button type="button" variant="outlined" onClick={onClose}>
          Close
        </Button>
        <Button
          type="button"
          variant="contained"
          className="mrc-cp52-prepare-dialog__confirm"
          onClick={async () => {
            if (typeof onConfirmPrepared === "function") {
              await onConfirmPrepared();
            }
          }}
        >
          Post for Billing
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MrcCp52PrepareDialog;
